import { useMutation } from '@apollo/client';
import {
  Button,
  Typography,
  Dialog,
  IconButton,
  TextField,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  CreateRequestedUnavailableProductsMutation,
  CreateRequestedProductsParams,
  CreateRequestedProductsResponse,
} from '@queries/requestedUnavaibleProducts/mutations';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@hooks/useAuth';
import { useToast } from '@hooks/toast.hook';
import { Console, getRoute } from '@utils';
import { getSentry } from '@services/sentry';
import { Product } from '@interfaces';
import { useRouter } from 'next/router';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useSegment } from '@hooks/useSegment';

const NotificationDialog = styled(Dialog)({
  borderRadius: '10px',
  '& .MuiDialog-paper': {
    borderRadius: '10px',
    minHeight: '328px',
    maxWidth: '438px',
  },
});
const NotificationTitle = styled(Typography)`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 17px;
  line-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  letter-spacing: 0.15px;
  color: #414046;
  padding: 3px 14px 12px 14px;
`;
const NotificationTextDialogDescription = styled(Typography)`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.15px;
  color: #414046;
  margin: 0px 14px 17px 14px;
  text-align: center;
`;
const SendButtonContainer = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const SendButton = styled(Button)`
  margin: 0px 0px 49px 0px;
  width: 200px;
  height: 42px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.15px;
  text-transform: none;
`;
const TextInput = styled(TextField)`
  margin-bottom: 30px;
`;
const InputDiv = styled('div')`
  margin: 0px 18px;
`;
const CheckIconStyled = styled(CheckIcon)`
  color: #00a650;
  height: 19.2px;
  width: 19.2px;
`;
const CheckCircleDiv = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1.2px solid #00a650;
  border-radius: 100px;
  width: 32px;
  height: 32px;
  margin: 17px 0px;
`;
const CheckIconDiv = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const NotificationSuccesTitle = styled(Typography)`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 17px;
  line-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  letter-spacing: 0.15px;
  color: #414046;
  padding: 0px 14px 12px 14px;
`;
const NotificationSuccesText = styled(Typography)`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.15px;
  color: #414046;
  margin: 0px 14px 30px 14px;
  text-align: center;
`;
const CloseButton = styled(IconButton)`
  position: absolute;
  right: 13px;
  top: 16px;
  color: #414046;
  width: 14px;
  height: 14px;
`;
const HeaderDiv = styled('div')`
  height: 46px;
`;

type FormInput = {
  email?: string;
};
interface ModalProps {
  openDialog: boolean;
  setClose: () => void;
  product: Product;
}
const TAG = '[PRODUCT_VIEW] ';
export const LetMeKnownWhenItsAvailableModal = (props: ModalProps) => {
  const { openDialog, setClose, product } = props;
  const { track } = useSegment();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
  } = useForm<FormInput>();
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const router = useRouter();
  const [sendRequest, { loading }] = useMutation<
    CreateRequestedProductsResponse,
    CreateRequestedProductsParams
  >(CreateRequestedUnavailableProductsMutation());
  const [requestSend, setRequestSend] = useState(false);
  const onSubmit = useCallback(async () => {
    try {
      void track('request know availability sent', {
        availability: product.availability?.status,
        brand: product.laboratory,
        id: product.id,
        name: product.name,
      });
      const data = getValues();
      Console.log({
        msg: TAG + 'Requesting unavaiable product',
        productId: product.id,
        notificationEmail: data.email,
      });
      await sendRequest({
        variables: {
          params: {
            productId: product.id,
            notificationEmail: data.email,
            quantity: 1,
          },
        },
      });
      setRequestSend(true);
    } catch (error) {
      await getSentry().then((Sentry) => {
        Sentry.captureException(error);
      });
      showToast({ message: 'Error al enviar la solicitud', type: 'error' });
    }
  }, [
    getValues,
    sendRequest,
    showToast,
    product.id,
    track,
    product.availability,
    product.name,
    product.laboratory,
  ]);

  return (
    <>
      <NotificationDialog
        open={openDialog}
        onClose={() => {
          setClose();
        }}
      >
        <HeaderDiv>
          <CloseButton
            aria-label="close"
            onClick={() => {
              setClose();
            }}
          >
            <CloseIcon />
          </CloseButton>
        </HeaderDiv>

        {requestSend ? (
          <>
            <CheckIconDiv>
              <CheckCircleDiv>
                <CheckIconStyled />
              </CheckCircleDiv>
            </CheckIconDiv>
            <NotificationSuccesTitle>
              Recordatorio creado
            </NotificationSuccesTitle>
            <NotificationSuccesText>
              Te enviaremos un correo cuando el producto esté disponible.
            </NotificationSuccesText>
            <SendButtonContainer>
              <SendButton
                variant="contained"
                onClick={() => {
                  void router.push(getRoute('home'));
                }}
              >
                Ir al inicio
              </SendButton>
            </SendButtonContainer>
          </>
        ) : (
          <>
            <NotificationTitle>Crear recordatorio</NotificationTitle>
            <NotificationTextDialogDescription>
              Completa los datos y te avisaremos cuando el producto vuelva a
              estar disponible.
            </NotificationTextDialogDescription>
            <form onSubmit={handleSubmit(onSubmit)}>
              <InputDiv>
                <TextInput
                  variant="outlined"
                  size="small"
                  {...register('email', {
                    required: 'Necesitamos tu correo electrónico',
                    pattern: {
                      value:
                        /^([a-zA-Z0-9_\-\+\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
                      message: 'Este email está incompleto 🤷‍♀',
                    },
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(
                        'email',
                        event.target.value.toLowerCase().trim(),
                      ),
                    value: appUser?.email || '',
                  })}
                  label="Correo electrónico"
                  error={Boolean(errors.email?.message)}
                  helperText={errors.email?.message}
                />
              </InputDiv>
              <SendButtonContainer>
                <SendButton
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  Enviar
                </SendButton>
              </SendButtonContainer>
            </form>
          </>
        )}
      </NotificationDialog>
    </>
  );
};
